import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon, Tag, RotateCcw } from "lucide-react";
import { format } from "date-fns";
import { th } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface AdminReportsFilterProps {
  search: string;
  setSearch: (q: string) => void;
  onSearch: () => void;
  statusFilter: string;
  setStatusFilter: (v: string) => void;
  learningStatusFilter: string;
  setLearningStatusFilter: (v: string) => void;
  submittedOnTimeFilter: string;
  setSubmittedOnTimeFilter: (v: string) => void;
  hoursDeductedFilter: string;
  setHoursDeductedFilter: (v: string) => void;
  teachingDateFrom: Date | undefined;
  setTeachingDateFrom: (d: Date | undefined) => void;
  teachingDateTo: Date | undefined;
  setTeachingDateTo: (d: Date | undefined) => void;
  showOtherSubjectsOnly: boolean;
  setShowOtherSubjectsOnly: (b: boolean) => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
}

export const AdminReportsFilter: React.FC<AdminReportsFilterProps> = ({
  search,
  setSearch,
  onSearch,
  statusFilter,
  setStatusFilter,
  learningStatusFilter,
  setLearningStatusFilter,
  submittedOnTimeFilter,
  setSubmittedOnTimeFilter,
  hoursDeductedFilter,
  setHoursDeductedFilter,
  teachingDateFrom,
  setTeachingDateFrom,
  teachingDateTo,
  setTeachingDateTo,
  showOtherSubjectsOnly,
  setShowOtherSubjectsOnly,
  resetFilters,
  hasActiveFilters,
}) => {
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") onSearch();
  };

  return (
    <div className="mb-4 space-y-3">
      {/* Search row */}
      <div className="flex w-full items-center gap-2">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="ค้นหาด้วยชื่อนักเรียน / ครูผู้สอน / รหัสครู / วิชา / อีเมล"
          onKeyDown={handleInputKeyDown}
          className="flex-1"
        />
        <Button type="button" onClick={onSearch}>ค้นหา</Button>
      </div>

      {/* Filters grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {/* Date range */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">วันที่สอน (จาก)</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn("w-full justify-start text-left font-normal", !teachingDateFrom && "text-muted-foreground")}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {teachingDateFrom ? format(teachingDateFrom, "d MMM yyyy", { locale: th }) : "เลือกวันที่"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={teachingDateFrom}
                onSelect={setTeachingDateFrom}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">วันที่สอน (ถึง)</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn("w-full justify-start text-left font-normal", !teachingDateTo && "text-muted-foreground")}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {teachingDateTo ? format(teachingDateTo, "d MMM yyyy", { locale: th }) : "เลือกวันที่"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={teachingDateTo}
                onSelect={setTeachingDateTo}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Status */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">สถานะ</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">ทั้งหมด</SelectItem>
              <SelectItem value="APPROVED">APPROVED</SelectItem>
              <SelectItem value="REJECTED">REJECTED</SelectItem>
              <SelectItem value="WAITING">WAITING</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Learning status */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">สถานะการเรียน</Label>
          <Select value={learningStatusFilter} onValueChange={setLearningStatusFilter}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">ทั้งหมด</SelectItem>
              <SelectItem value="NORMAL">เข้าเรียน/เข้าสอนปกติ</SelectItem>
              <SelectItem value="ABSENT">น้องไม่เข้าเรียน (ลากะทันหัน)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Submitted on time */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">ส่งตรงเวลา</Label>
          <Select value={submittedOnTimeFilter} onValueChange={setSubmittedOnTimeFilter}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">ทั้งหมด</SelectItem>
              <SelectItem value="YES">ส่งตรงเวลา</SelectItem>
              <SelectItem value="NO">ส่งไม่ตรงเวลา</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Hours deducted */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">หัก ชม.</Label>
          <Select value={hoursDeductedFilter} onValueChange={setHoursDeductedFilter}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">ทั้งหมด</SelectItem>
              <SelectItem value="YES">หักแล้ว</SelectItem>
              <SelectItem value="NO">ยังไม่หัก</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Bottom row: checkbox + reset */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 px-3 py-2 border rounded-md bg-background">
          <Checkbox
            id="showOtherSubjectsOnly"
            checked={showOtherSubjectsOnly}
            onCheckedChange={(checked) => setShowOtherSubjectsOnly(checked as boolean)}
          />
          <Label htmlFor="showOtherSubjectsOnly" className="text-sm cursor-pointer flex items-center gap-1">
            <Tag className="h-3 w-3 text-amber-600" />
            <span>แสดงเฉพาะหัวข้ออื่นๆ</span>
          </Label>
        </div>

        {hasActiveFilters && (
          <Button variant="outline" size="sm" onClick={resetFilters}>
            <RotateCcw className="h-4 w-4 mr-1" />
            รีเซ็ตตัวกรอง
          </Button>
        )}
      </div>
    </div>
  );
};
